import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { ClockCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, AllSettlementClientResponse } from '../../graphql/queries/getAllSettlementClient';
import InputFilterItem from "../../../../delivery/components/InputFilterItem";
import ParcelFilterStyle from "../../../../delivery/components/ParcelFilterStyle";
import DateRangeFilterItem from "../../../../delivery/components/DateRangeFilterItem";
import { transactionTypeOptions, transactionResultOptions } from "../config/filterOptions";
import { TransactionFlowRecordFilterType } from '../graphql/types/TransactionFlowRecordType';
import { SelectOptionType } from 'graphql/types/SelectOptionType';
import { ChannelSelectFilter } from '../../../../../components/settlement/ChannelSelectFilter';
import { TimezoneFilterItem } from '../../../../../components/TimezoneFilterItem';
import { BILL_TIMEZONE_OPTIONS } from '../../../../../utils/timezoneFormatter';
import { buildDateOnlyTimeRange } from '../../../../../utils/dateTimeUtils';
import { CREATE_TRANSACTION_FLOW_RECORD_DOWNLOAD_TASK, TransactionFlowRecordDownloadTaskResponse } from '../graphql/mutations/createTransactionFlowRecordDownloadTask';
import ParcelsDownloadModal from '../../components/ParcelsDownloadModal';
import { ResourceCode } from '../../../../../constants/resourceCodes';
import { HasResource } from '../../../../../components/HasResource';

interface TransactionFlowRecordFilterProps {
    filters: TransactionFlowRecordFilterType;
    setFilters: (filters: TransactionFlowRecordFilterType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    onAdjustmentClick: () => void;
}

/** 交易时间筛选可选时区，不选则由后端按 UTC 处理 */
const timeZoneOptions = BILL_TIMEZONE_OPTIONS.map(({ value, label }) => ({ value, label }));

const TransactionFlowRecordFilter: React.FC<TransactionFlowRecordFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
    onAdjustmentClick,
}) => {
    const [settlementClientOptions, setSettlementClientOptions] = useState<SelectOptionType[]>([]);
    const { data: settlementClientList } = useQuery<AllSettlementClientResponse>(GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间

    // 下载完成模态框状态
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');

    // 使用mutation来创建下载任务
    const [createTransactionFlowRecordDownloadTask] = useMutation<TransactionFlowRecordDownloadTaskResponse>(CREATE_TRANSACTION_FLOW_RECORD_DOWNLOAD_TASK);

    // 从localStorage获取上次下载时间，页面刷新后仍然有效
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('transactionFlowRecordDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('transactionFlowRecordDownloadLastTime', time.toString());
    };

    useEffect(() => {
        if (settlementClientList) {
            const list = Array.isArray(settlementClientList.allOptions) ? settlementClientList.allOptions : [settlementClientList.allOptions];
            setSettlementClientOptions(list);
        }
    }, [settlementClientList]);

    // 冷却时间倒计时（页面刷新后仍然有效）
    useEffect(() => {
        const checkCooldown = () => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const currentTime = Date.now();
                const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);

                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem('transactionFlowRecordDownloadLastTime'); // 清理过期数据
                }
            } else {
                setCooldownRemaining(0);
            }
        };

        // 立即检查一次（页面刷新后）
        checkCooldown();
        // 启动定时器，每秒更新一次
        const interval = setInterval(checkCooldown, 1000);
        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);

    // 处理时间变化的函数
    const handleTransactionTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({ ...filters, transactionTime: dates });
    };

    // 构建查询参数：transactionTime 只转换成 { start, end } 墙钟时间，timeZoneId 作为独立参数原样透传给后端
    const buildQueryFilters = (filters: TransactionFlowRecordFilterType) => {
        const { timeZoneId, transactionTime, ...rest } = filters;
        return {
            ...rest,
            transactionTime: buildDateOnlyTimeRange(transactionTime, timeZoneId),
            timeZoneId
        };
    };

    // Download handler function
    const handleDownloadTransactionFlowRecord = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        // Check cooldown time
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again to avoid duplicate requests`);
            return;
        }
        // Check if already loading
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime); // Store to localStorage
            // 使用与filter按钮完全相同的参数构建逻辑
            const queryFilters = buildQueryFilters(filters);
            const response = await createTransactionFlowRecordDownloadTask({
                variables: {
                    filters: queryFilters,
                }
            });
            if (response.data?.createTransactionFlowRecordDownloadTask) {
                setGeneratedFileName(response.data?.createTransactionFlowRecordDownloadTask);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error) {
            console.error('Create TransactionFlowRecord Download Task Failed:', error);
            message.error('Create TransactionFlowRecord Download Task Failed, Please Try Again Later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="mb-6">
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-4">
                <ParcelFilterStyle
                    label="交易类型"
                    options={transactionTypeOptions}
                    value={filters.transactionType ?? undefined}
                    onChange={(value: string) => setFilters({ ...filters, transactionType: value ? value : undefined })}
                    spanClassName="w-[9em]"
                />
                <ChannelSelectFilter
                    label="客户名称"
                    value={filters.clientId ?? undefined}
                    onChange={(value: string | undefined) => setFilters({...filters, clientId: value})}
                    spanClassName="w-[9em]"
                />
                <ParcelFilterStyle
                    label="交易状态"
                    options={transactionResultOptions}
                    value={filters.transactionResult ?? undefined}
                    onChange={(value: string) => setFilters({ ...filters, transactionResult: value || undefined })}
                    spanClassName="w-[9em]"
                />
                <InputFilterItem
                    label="业务订单号"
                    value={filters.bizOrderNo || undefined}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, bizOrderNo: e.target.value || undefined })}
                    spanClassName="w-[9em]"
                />
                <InputFilterItem
                    label="结算交易号"
                    value={filters.businessId || undefined}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, businessId: e.target.value || undefined })}
                    spanClassName="w-[9em]"
                />
                <InputFilterItem
                    label="结算交易ID"
                    value={filters.transactionNo || undefined}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, transactionNo: e.target.value || undefined })}
                    spanClassName="w-[9em]"
                />
                <DateRangeFilterItem
                    label="交易时间"
                    value={filters.transactionTime}
                    onChange={handleTransactionTimeChange}
                    spanClassName="w-[9em]"
                />
                <TimezoneFilterItem
                    label="时区"
                    options={timeZoneOptions}
                    value={filters.timeZoneId}
                    onChange={(value) => setFilters({ ...filters, timeZoneId: value })}
                    placeholder="UTC (默认)"
                    spanClassName="w-[9em]"
                />
            </div>

            <div className="mb-4 flex justify-between">
                <div className="space-x-4">
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_TRANS_QUERY]}>
                        <Button
                            type="primary"
                            onClick={handleDownloadTransactionFlowRecord}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            icon={cooldownRemaining > 0 ? <ClockCircleOutlined/> : <DownloadOutlined/>}
                        >
                            {cooldownRemaining > 0
                                ? `Download ${cooldownRemaining}s`
                                : 'Download'
                            }
                        </Button>
                    </HasResource>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_TRANS_QUERY]}>
                        <Button type="primary" onClick={handleFilter}>
                            Filter
                        </Button>
                    </HasResource>
                    <Button onClick={handleResetFilters}>
                        Reset
                    </Button>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_TRANS_QUERY]}>
                        <Button onClick={onAdjustmentClick}>
                            Adjustment
                        </Button>
                    </HasResource>
                </div>
            </div>

            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default TransactionFlowRecordFilter;


